export type RoundingMethod = 'ROUND_HALF_AWAY_FROM_ZERO' | 'ROUND_AWAY_FROM_ZERO';
export class RoundingMethods {
  static ROUND_HALF_AWAY_FROM_ZERO: RoundingMethod = 'ROUND_HALF_AWAY_FROM_ZERO';
  static ROUND_AWAY_FROM_ZERO: RoundingMethod = 'ROUND_AWAY_FROM_ZERO';
}

export class MathUtils {
  static roundAndFormatAsString(original: number, method: RoundingMethod, decimals = 2, step?: number, trimZeros = false): string {
    if (original == null || isNaN(original))
      return '';

    const initialDecimals = step != null ? MathUtils.countDecimalPlaces(step) : decimals;

    let formattedValue = MathUtils.round(+original, method, decimals, step).toFixed(initialDecimals);

    if (trimZeros) {
      formattedValue = formattedValue.replace(/\.[0-9]+$/, text => text.replace(/0+$/, ''));
    }

    while (MathUtils.countDecimalPlaces(formattedValue) < decimals) {
      if (MathUtils.countDecimalPlaces(formattedValue) === 0 && decimals > 0 && formattedValue[formattedValue.length - 1] !== '.') {
        formattedValue = formattedValue + '.';
      }
      formattedValue = formattedValue + '0';
    }

    formattedValue = formattedValue.replace(/\.$/, '');

    return formattedValue;
  }

  /**
   * Returns a rounded copy of an @param original number using the specified RoundingMethod.
   * If @param step is specified, @param decimals will be ignored, and we will round by step;
   * otherwise, round by decimal places.
   */
  static round(original: number, method: RoundingMethod, decimals = 2, step?: number): number {
    if (method === 'ROUND_HALF_AWAY_FROM_ZERO')
      return MathUtils.roundHalfAwayFromZero(original, decimals, step);
    if (method === 'ROUND_AWAY_FROM_ZERO')
      return MathUtils.roundAwayFromZero(original, decimals, step);
    throw new Error(`Unhandled RoundingMethod ${method}`);
  }

  static roundAwayFromZero(original: number, decimals = 2, step?: number): number {
    const factor = step ? 1 / step : Math.pow(10, decimals);

    if (original < 0)
      return (-(Math.ceil(-original * factor) / factor));

    return (Math.ceil(original * factor) / factor);
  }

  static roundHalfAwayFromZero(original: number, decimals = 2, step?: number): number {
    const factor = step ? 1 / step : Math.pow(10, decimals);

    if (original < 0)
      return (-(Math.round(-original * factor) / factor));

    return (Math.round(original * factor) / factor);
  }

  static countDecimalPlaces(num: number | string): number {
    if (num == null) return null;

    const str = `${num}`;

    if (!str.includes('.'))
      return 0;

    return str.length - str.indexOf('.') - 1;
  }
}


export function roundingMethodFromRoundingMode(roundingMode: string): RoundingMethod | null {
  if (roundingMode === 'UP')
    return RoundingMethods.ROUND_AWAY_FROM_ZERO;

  if (roundingMode === 'HALF_UP')
    return RoundingMethods.ROUND_HALF_AWAY_FROM_ZERO;

  return null;
}
